import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common'; // Import CommonModule

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
}

@Component({
  selector: 'app-snow-particle-effect',
  templateUrl: './snow-particle-effect.component.html',
  styleUrls: ['./snow-particle-effect.component.css'],
  standalone: true // Mark as standalone
})
export class SnowParticleEffectComponent implements AfterViewInit, OnDestroy {
  @ViewChild('snowCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  private ctx!: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private animationFrameId?: number;

  private readonly particleCount = 100; // Number of snowflakes

  constructor() { }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) {
      console.error('Failed to get 2D context');
      return;
    }
    this.ctx = context;

    this.resizeCanvas();
    this.initParticles();
    this.animate();

    window.addEventListener('resize', this.resizeCanvas);
  }

  ngOnDestroy(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    window.removeEventListener('resize', this.resizeCanvas);
  }

  private resizeCanvas = (): void => {
    const canvas = this.canvasRef.nativeElement;
    const parent = canvas.parentElement;
    if (parent) {
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
    }
  }

  private initParticles(): void {
    const canvas = this.canvasRef.nativeElement;
    this.particles = [];
    for (let i = 0; i < this.particleCount; i++) {
      this.particles.push(this.createParticle(true));
    }
  }

  private createParticle(isInitial: boolean): Particle {
    const canvas = this.canvasRef.nativeElement;
    return {
      x: Math.random() * canvas.width,
      y: isInitial ? Math.random() * canvas.height : -10, // Start at top or randomly spread initially
      vx: (Math.random() - 0.5) * 0.5, // Horizontal drift
      vy: Math.random() * 0.07 + 0.03,   // Speed as per user's request
      radius: Math.random() * 2 + 1,  // Snowflake size
      opacity: Math.random() * 0.7 + 0.3 // Snowflake opacity
    };
  }

  private animate = (): void => {
    this.draw();
    this.animationFrameId = requestAnimationFrame(this.animate);
  }

  private draw(): void {
    const canvas = this.canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);

    this.particles.forEach(p => {
      p.x += p.vx;
      p.y += p.vy;

      // Reset particle when it falls off screen
      if (p.y > canvas.height + 10) {
        Object.assign(p, this.createParticle(false));
      }

      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2, false);
      this.ctx.fillStyle = `rgba(255, 255, 255, ${p.opacity})`;
      this.ctx.fill();
    });
  }
}
